import { useState, useEffect, useMemo, useRef } from "react";
import { ChevronLeft, ChevronRight, Loader2, Check, Tag, ShieldCheck, KeyRound, MapPin, Clock } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { api, euro, apiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";

const WD_DE = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const WD_EN = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MON_DE = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];

const fmtISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const parseISO = (s) => { const [y, m, dd] = s.split("-").map(Number); return new Date(y, m - 1, dd); };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfWeek = (d) => { const x = new Date(d); const wd = (x.getDay() + 6) % 7; return addDays(x, -wd); };

const TrackManBuchen = () => {
  const { lang, t: cms } = useLang();
  const tm = cms.trackman || {};
  const t = (de, en) => (lang === "de" ? de : en);
  const WD = lang === "de" ? WD_DE : WD_EN;

  const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);
  const [view, setView] = useState("week");
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [monthAnchor, setMonthAnchor] = useState(() => new Date());
  const [selectedDay, setSelectedDay] = useState(null); // ISO string (month view)
  const [days, setDays] = useState([]);
  const [prices, setPrices] = useState({ price_member: 30, price_guest: 40, location: "", resource_name: "TrackMan Scope-Hütte" });
  const [loading, setLoading] = useState(true);

  const [isMember, setIsMember] = useState(false);
  const [sel, setSel] = useState({ date: null, hours: [] });
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", street: "", postcode: "", city: "", country: "Deutschland" });
  const [videoOk, setVideoOk] = useState(false);
  const [keyOk, setKeyOk] = useState(false);
  const [code, setCode] = useState("");
  const [discount, setDiscount] = useState(null); // {percent, total, discount_amount}
  const [codeBusy, setCodeBusy] = useState(false);
  const [payBusy, setPayBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [ppConfig, setPpConfig] = useState(null);
  const panelRef = useRef(null);

  useEffect(() => { api.get("/payments/paypal-config").then((r) => setPpConfig(r.data)).catch(() => setPpConfig({ enabled: false })); }, []);

  const range = useMemo(() => {
    if (view === "week") return { start: weekStart, end: addDays(weekStart, 6) };
    const first = new Date(monthAnchor.getFullYear(), monthAnchor.getMonth(), 1);
    const last = new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() + 1, 0);
    return { start: startOfWeek(first), end: addDays(startOfWeek(last), 6) };
  }, [view, weekStart, monthAnchor]);

  const loadAvail = () => {
    setLoading(true);
    api.get(`/trackman/availability?start=${fmtISO(range.start)}&end=${fmtISO(range.end)}`)
      .then((r) => { setDays(r.data.days); setPrices(r.data); })
      .catch(() => toast.error(t("Verfügbarkeit konnte nicht geladen werden", "Could not load availability")))
      .finally(() => setLoading(false));
  };
  useEffect(loadAvail, [range.start.getTime(), range.end.getTime()]); // eslint-disable-line

  // Kalender einmalig auf das Freigabedatum springen, falls Buchungen erst später starten
  const jumpedRef = useRef(false);
  useEffect(() => {
    const bs = prices.booking_start_date;
    if (bs && !jumpedRef.current) {
      const bsd = parseISO(bs);
      if (bsd > today) {
        jumpedRef.current = true;
        setWeekStart(startOfWeek(bsd));
        setMonthAnchor(new Date(bsd.getFullYear(), bsd.getMonth(), 1));
      } else {
        jumpedRef.current = true;
      }
    }
  }, [prices.booking_start_date]); // eslint-disable-line

  const bookingBlockedUntil = prices.booking_start_date && parseISO(prices.booking_start_date) > today ? prices.booking_start_date : "";

  const dayMap = useMemo(() => Object.fromEntries(days.map((d) => [d.date, d])), [days]);
  const pricePerHour = isMember ? prices.price_member : prices.price_guest;
  const baseTotal = pricePerHour * sel.hours.length;
  const finalTotal = discount ? discount.total : baseTotal;
  const contiguous = sel.hours.length === 0 || sel.hours.slice(1).every((h, i) => h === sel.hours[i] + 1);

  // Rabatt zurücksetzen, wenn sich Auswahl/Mitgliedstatus ändert
  useEffect(() => { setDiscount(null); }, [sel.date, sel.hours.join(","), isMember]);

  // Sanft zum Buchungsfenster scrollen, sobald ein Tag ausgewählt wird
  useEffect(() => {
    if (sel.date && panelRef.current) {
      const id = setTimeout(() => panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
      return () => clearTimeout(id);
    }
  }, [sel.date]); // eslint-disable-line

  const toggleSlot = (date, hour) => {
    setSel((prev) => {
      if (prev.date !== date) return { date, hours: [hour] };
      const has = prev.hours.includes(hour);
      const hours = has ? prev.hours.filter((h) => h !== hour) : [...prev.hours, hour];
      return { date: hours.length ? date : null, hours: hours.sort((a, b) => a - b) };
    });
  };

  const isPast = (dateISO, hour) => {
    const d = parseISO(dateISO); d.setHours(hour, 0, 0, 0);
    return d <= new Date();
  };

  const slotBtn = (dateISO, s) => {
    const disabledPast = isPast(dateISO, s.hour);
    const selected = sel.date === dateISO && sel.hours.includes(s.hour);
    const free = s.status === "free" && !disabledPast;
    let cls = "w-full text-left px-3 py-2 text-xs font-mono-lab tracking-wide transition-colors border ";
    if (selected) cls += "bg-[#ff0033] border-[#ff0033] text-white";
    else if (free) cls += "bg-green-500/10 border-green-500/40 text-green-300 hover:bg-green-500/20 cursor-pointer";
    else if (s.status === "booked") cls += "bg-[#ff0033]/10 border-[#ff0033]/30 text-[#ff6b7f] opacity-70 cursor-not-allowed line-through";
    else cls += "bg-white/5 border-white/10 text-neutral-600 cursor-not-allowed";
    return (
      <button key={s.hour} type="button" disabled={!free && !selected} onClick={() => toggleSlot(dateISO, s.hour)}
        data-testid={`tm-slot-${dateISO}-${s.hour}`} className={cls}>
        {s.start}–{s.end}
      </button>
    );
  };

  const applyCode = async () => {
    if (!code.trim()) return;
    if (!sel.date) { toast.error(t("Bitte zuerst Slots auswählen", "Please select slots first")); return; }
    setCodeBusy(true);
    try {
      const { data } = await api.post("/trackman/discount/validate", { code: code.trim(), date: sel.date, hours: sel.hours, is_member: isMember, email: form.email });
      setDiscount(data);
      toast.success(t(`Rabatt ${data.percent}% angewendet`, `Discount ${data.percent}% applied`));
    } catch (e) { setDiscount(null); toast.error(apiError(e)); }
    finally { setCodeBusy(false); }
  };

  const validate = () => {
    if (!sel.date || !sel.hours.length) { toast.error(t("Bitte mindestens einen Zeit-Slot auswählen", "Please select a time slot")); return false; }
    if (!contiguous) { toast.error(t("Bitte nur direkt aufeinanderfolgende Stunden wählen", "Please select consecutive hours only")); return false; }
    if (!form.first_name || !form.last_name || !form.email) { toast.error(t("Bitte Name und E-Mail angeben", "Please enter name and email")); return false; }
    if (!form.street || !form.postcode || !form.city) { toast.error(t("Bitte vollständige Rechnungsadresse angeben", "Please enter full billing address")); return false; }
    if (!videoOk || !keyOk) { toast.error(t("Bitte beide Pflicht-Einwilligungen bestätigen", "Please confirm both mandatory consents")); return false; }
    return true;
  };
  const buildPayload = () => ({ date: sel.date, hours: sel.hours, is_member: isMember, ...form, discount_code: discount ? code.trim() : "", video_consent: videoOk, key_consent: keyOk, origin_url: window.location.origin });
  const validateRef = useRef(validate); validateRef.current = validate;
  const payloadRef = useRef(buildPayload); payloadRef.current = buildPayload;

  const payStripe = async () => {
    if (!validate()) return;
    setPayBusy(true);
    try {
      const { data } = await api.post("/trackman/checkout/stripe", buildPayload());
      if (data.free) { setDone({ booking_number: data.booking_number, email: data.email }); setPayBusy(false); return; }
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e)); setPayBusy(false); }
  };

  const inputCls = "w-full bg-white text-black border border-neutral-300 p-3 focus:border-[#ff0033] outline-none text-sm placeholder:text-neutral-400";

  if (done) return <DoneView done={done} lang={lang} />;

  const selStartEnd = sel.hours.length ? `${String(sel.hours[0]).padStart(2, "0")}:00–${sel.hours[sel.hours.length - 1] === 23 ? "24" : String(sel.hours[sel.hours.length - 1] + 1).padStart(2, "0")}:00` : "";

  return (
    <div data-testid="trackman-page" className="bg-[#050505] min-h-screen">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="/media/trackman-hero.jpeg" alt="TrackMan" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/80 to-[#050505]/30" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-16">
          <span className="inline-flex items-center gap-2 font-mono-lab text-[10px] tracking-[0.25em] uppercase text-[#ff0033] border border-[#ff0033]/40 px-3 py-1.5" data-testid="tm-kicker">
            <span className="w-1.5 h-1.5 bg-[#ff0033] rounded-full animate-pulse" /> {tm.kicker || "TrackMan Scope-Hütte"}
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight text-white mt-5 leading-[0.95]">
            {tm.title || t("TrackMan Scope-Hütte buchen", "Book the TrackMan Scope hut")}
          </h1>
          <p className="text-neutral-300 text-base md:text-lg max-w-2xl mt-5 leading-relaxed">
            {tm.sub || t("Buche Deine Stunde auf der Driving Range des Golfclub Kirchheim-Wendlingen – täglich von 20:00 bis 24:00 Uhr. Wähle einen oder mehrere aufeinanderfolgende Stunden-Slots. Verfügbar nach erfolgreicher Vorabzahlung.",
               "Book your hour at the Golfclub Kirchheim-Wendlingen driving range – daily from 8pm to midnight. Pick one or more consecutive hourly slots. Available after successful prepayment.")}
          </p>
          <div className="flex items-center gap-4 mt-5 text-sm text-neutral-400">
            <span className="flex items-center gap-2"><MapPin size={15} className="text-[#ff0033]" /> {prices.location || tm.locationFallback || "Driving Range GC Kirchheim-Wendlingen"}</span>
          </div>
          {/* Preisübersicht */}
          <div className="grid grid-cols-2 gap-4 mt-8 max-w-md">
            <div className="bg-white/5 border border-white/10 p-4" data-testid="tm-price-member">
              <p className="font-mono-lab text-[10px] uppercase tracking-widest text-neutral-500">{tm.memberLabel || t("Mitglied GC KW", "Member GC KW")}</p>
              <p className="font-heading text-3xl text-white mt-1">{euro(prices.price_member)}<span className="text-sm text-neutral-500">/{t("Std.", "hr")}</span></p>
            </div>
            <div className="bg-white/5 border border-white/10 p-4" data-testid="tm-price-guest">
              <p className="font-mono-lab text-[10px] uppercase tracking-widest text-neutral-500">{tm.guestLabel || t("Gast / Nichtmitglied", "Guest / non-member")}</p>
              <p className="font-heading text-3xl text-white mt-1">{euro(prices.price_guest)}<span className="text-sm text-neutral-500">/{t("Std.", "hr")}</span></p>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
        {bookingBlockedUntil && (
          <div className="flex items-center gap-3 bg-[#ff0033]/10 border border-[#ff0033]/40 px-4 py-3 text-sm text-white" data-testid="tm-blocked-banner">
            <Clock size={18} className="text-[#ff0033] shrink-0" />
            <span>{t(`Buchungen sind erst ab dem ${parseISO(bookingBlockedUntil).getDate()}.${parseISO(bookingBlockedUntil).getMonth() + 1}.${parseISO(bookingBlockedUntil).getFullYear()} möglich. Termine davor sind noch gesperrt.`,
                     `Bookings are only available from ${parseISO(bookingBlockedUntil).getDate()}/${parseISO(bookingBlockedUntil).getMonth() + 1}/${parseISO(bookingBlockedUntil).getFullYear()}. Earlier slots are still blocked.`)}</span>
          </div>
        )}
        {/* KALENDER */}
        <div>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <div className="flex items-center gap-2">
              <button onClick={() => setView("week")} data-testid="tm-view-week" className={`px-4 py-2 font-mono-lab text-xs uppercase tracking-widest border ${view === "week" ? "bg-[#ff0033] border-[#ff0033] text-white" : "border-white/15 text-neutral-300"}`}>{t("Woche", "Week")}</button>
              <button onClick={() => setView("month")} data-testid="tm-view-month" className={`px-4 py-2 font-mono-lab text-xs uppercase tracking-widest border ${view === "month" ? "bg-[#ff0033] border-[#ff0033] text-white" : "border-white/15 text-neutral-300"}`}>{t("Monat", "Month")}</button>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid="tm-prev" onClick={() => view === "week" ? setWeekStart(addDays(weekStart, -7)) : setMonthAnchor(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() - 1, 1))} className="p-2 border border-white/15 text-neutral-300 hover:text-[#ff0033]"><ChevronLeft size={16} /></button>
              <span className="font-mono-lab text-xs uppercase tracking-widest text-white min-w-[160px] text-center" data-testid="tm-range-label">
                {view === "week" ? `${weekStart.getDate()}.${weekStart.getMonth() + 1}. – ${addDays(weekStart, 6).getDate()}.${addDays(weekStart, 6).getMonth() + 1}.` : `${MON_DE[monthAnchor.getMonth()]} ${monthAnchor.getFullYear()}`}
              </span>
              <button data-testid="tm-next" onClick={() => view === "week" ? setWeekStart(addDays(weekStart, 7)) : setMonthAnchor(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() + 1, 1))} className="p-2 border border-white/15 text-neutral-300 hover:text-[#ff0033]"><ChevronRight size={16} /></button>
            </div>
          </div>

          {/* Legende */}
          <div className="flex flex-wrap gap-4 mb-4 text-[11px] font-mono-lab uppercase tracking-widest text-neutral-400">
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-green-500/40 border border-green-500" /> {t("Frei", "Free")}</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-[#ff0033]/30 border border-[#ff0033]" /> {t("Gebucht", "Booked")}</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-white/10 border border-white/20" /> {t("Gesperrt", "Blocked")}</span>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-[#ff0033]" /></div>
          ) : view === "week" ? (
            <div className="grid grid-cols-1 sm:grid-cols-7 gap-2" data-testid="tm-week-grid">
              {Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)).map((d) => {
                const ds = fmtISO(d);
                const day = dayMap[ds];
                const isToday = fmtISO(new Date()) === ds;
                return (
                  <div key={ds} className="border border-white/10 bg-[#0C0C0C]">
                    <div className={`px-2 py-2 text-center border-b border-white/10 ${isToday ? "bg-[#ff0033]/15" : ""}`}>
                      <p className="font-mono-lab text-[10px] uppercase tracking-widest text-neutral-500">{WD[(d.getDay() + 6) % 7]}</p>
                      <p className="font-heading text-lg text-white leading-none mt-0.5">{d.getDate()}.{d.getMonth() + 1}.</p>
                    </div>
                    <div className="p-2 space-y-1.5 min-h-[80px]">
                      {!day || day.closed || !day.slots.length ? (
                        <p className="text-neutral-600 text-[10px] font-mono-lab text-center py-3">{t("Geschlossen", "Closed")}</p>
                      ) : day.slots.map((s) => slotBtn(ds, s))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <MonthGrid monthAnchor={monthAnchor} dayMap={dayMap} WD={WD} selectedDay={selectedDay} onPick={(ds) => setSelectedDay(ds)} t={t} />
          )}

          {view === "month" && selectedDay && (
            <div className="mt-5 border border-white/10 bg-[#0C0C0C] p-4" data-testid="tm-month-day-slots">
              <p className="font-heading text-xl text-white mb-3">{parseISO(selectedDay).getDate()}. {MON_DE[parseISO(selectedDay).getMonth()]} {parseISO(selectedDay).getFullYear()}</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {(dayMap[selectedDay]?.slots || []).length ? dayMap[selectedDay].slots.map((s) => slotBtn(selectedDay, s)) : <p className="text-neutral-500 text-sm col-span-4">{t("An diesem Tag keine Öffnungszeiten.", "No opening hours on this day.")}</p>}
              </div>
            </div>
          )}
        </div>

        {/* BUCHUNGS-PANEL */}
        <div className="max-w-2xl mx-auto w-full" ref={panelRef}>
          <div className="bg-white text-black border border-neutral-200" data-testid="tm-booking-panel">
            <div className="bg-[#ff0033] text-white px-5 py-4">
              <p className="font-heading uppercase tracking-wide text-lg leading-none">{tm.bookingTitle || t("Deine Buchung", "Your booking")}</p>
            </div>
            <div className="p-5 space-y-4">
              {/* Mitglied Toggle */}
              <div>
                <p className="font-mono-lab text-[10px] uppercase tracking-widest text-neutral-500 mb-2">{tm.memberQuestion || t("Mitglied GC Kirchheim-Wendlingen?", "Member of GC Kirchheim-Wendlingen?")}</p>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => setIsMember(true)} data-testid="tm-member-yes" className={`py-2.5 font-mono-lab text-xs uppercase tracking-widest border ${isMember ? "bg-[#ff0033] border-[#ff0033] text-white" : "border-neutral-300 text-neutral-700"}`}>{t("Ja", "Yes")} · {euro(prices.price_member)}</button>
                  <button onClick={() => setIsMember(false)} data-testid="tm-member-no" className={`py-2.5 font-mono-lab text-xs uppercase tracking-widest border ${!isMember ? "bg-[#ff0033] border-[#ff0033] text-white" : "border-neutral-300 text-neutral-700"}`}>{t("Nein", "No")} · {euro(prices.price_guest)}</button>
                </div>
              </div>

              {/* Auswahl-Zusammenfassung */}
              {sel.date ? (
                <div className="bg-neutral-100 p-3 text-sm" data-testid="tm-summary">
                  <div className="flex justify-between py-0.5"><span className="text-neutral-500">{t("Datum", "Date")}</span><span className="font-semibold">{parseISO(sel.date).getDate()}.{parseISO(sel.date).getMonth() + 1}.{parseISO(sel.date).getFullYear()}</span></div>
                  <div className="flex justify-between py-0.5"><span className="text-neutral-500">{t("Zeit", "Time")}</span><span className="font-semibold">{selStartEnd}</span></div>
                  <div className="flex justify-between py-0.5"><span className="text-neutral-500">{t("Stunden", "Hours")}</span><span className="font-semibold">{sel.hours.length}</span></div>
                  <div className="flex justify-between py-0.5"><span className="text-neutral-500">{t("Preis / Std.", "Price / hr")}</span><span>{euro(pricePerHour)}</span></div>
                  {discount && <div className="flex justify-between py-0.5 text-[#ff0033]"><span>{t("Rabatt", "Discount")} ({discount.percent}%)</span><span>−{euro(discount.discount_amount)}</span></div>}
                  <div className="flex justify-between py-1.5 mt-1 border-t border-neutral-300 font-heading text-lg"><span>{t("Gesamt", "Total")}</span><span data-testid="tm-total">{euro(finalTotal)}</span></div>
                  {!contiguous && <p className="text-[#ff0033] text-xs mt-1">{t("Bitte nur aufeinanderfolgende Stunden wählen.", "Please select consecutive hours.")}</p>}
                </div>
              ) : (
                <p className="text-neutral-500 text-sm bg-neutral-100 p-3" data-testid="tm-no-selection">{t("Bitte wähle links einen freien Zeit-Slot.", "Please pick a free slot on the left.")}</p>
              )}

              {sel.date && contiguous && (
                <>
                  {/* Kundendaten */}
                  <div className="grid grid-cols-2 gap-2">
                    <input className={inputCls} placeholder={t("Vorname", "First name")} value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="tm-first-name" />
                    <input className={inputCls} placeholder={t("Nachname", "Last name")} value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="tm-last-name" />
                  </div>
                  <input className={inputCls} placeholder={t("E-Mail", "Email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="tm-email" />
                  <input className={inputCls} placeholder={t("Telefon", "Phone")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="tm-phone" />
                  <input className={inputCls} placeholder={t("Straße & Hausnummer", "Street & no.")} value={form.street} onChange={(e) => setForm({ ...form, street: e.target.value })} data-testid="tm-street" />
                  <div className="grid grid-cols-3 gap-2">
                    <input className={inputCls} placeholder={t("PLZ", "ZIP")} value={form.postcode} onChange={(e) => setForm({ ...form, postcode: e.target.value })} data-testid="tm-postcode" />
                    <input className={`${inputCls} col-span-2`} placeholder={t("Ort", "City")} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="tm-city" />
                  </div>
                  <input className={inputCls} placeholder={t("Land", "Country")} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="tm-country" />

                  {/* Rabattcode */}
                  <div className="flex gap-2">
                    <input className={inputCls} placeholder={t("Rabattcode", "Discount code")} value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} data-testid="tm-code-input" />
                    <button onClick={applyCode} disabled={codeBusy} data-testid="tm-code-apply" className="shrink-0 bg-black text-white px-4 font-mono-lab text-xs uppercase tracking-widest hover:bg-neutral-800 disabled:opacity-50 flex items-center gap-1"><Tag size={13} /> {t("Anwenden", "Apply")}</button>
                  </div>

                  {/* Pflicht-Checkboxen */}
                  <label className="flex items-start gap-2.5 text-xs text-neutral-700 cursor-pointer" data-testid="tm-consent-video-label">
                    <input type="checkbox" checked={videoOk} onChange={(e) => setVideoOk(e.target.checked)} className="mt-0.5 accent-[#ff0033]" data-testid="tm-consent-video" />
                    <span className="flex-1"><ShieldCheck size={13} className="inline mr-1 text-[#ff0033]" />{tm.consentVideo || t("Ich bestätige, dass ich darüber informiert wurde, dass die TrackMan Scope-Hütte und der Zugangsbereich aus Sicherheitsgründen videoüberwacht werden können.", "I confirm I have been informed that the TrackMan Scope hut and access area may be video-monitored for security reasons.")}</span>
                  </label>
                  <label className="flex items-start gap-2.5 text-xs text-neutral-700 cursor-pointer" data-testid="tm-consent-key-label">
                    <input type="checkbox" checked={keyOk} onChange={(e) => setKeyOk(e.target.checked)} className="mt-0.5 accent-[#ff0033]" data-testid="tm-consent-key" />
                    <span className="flex-1"><KeyRound size={13} className="inline mr-1 text-[#ff0033]" />{tm.consentKey || t("Ich verpflichte mich, den Schlüssel nach der Nutzung unverzüglich und ordnungsgemäß in die Schlüsselbox zurückzulegen. Mir ist bekannt, dass bei Verlust oder Nicht-Rückgabe Kosten entstehen können.", "I commit to returning the key to the key box promptly and properly after use. I am aware that costs may arise in case of loss or non-return.")}</span>
                  </label>

                  {/* Zahlung */}
                  {finalTotal <= 0 ? (
                    <button onClick={payStripe} disabled={payBusy} data-testid="tm-pay-free" className="w-full bg-[#ff0033] text-white font-heading uppercase tracking-wider py-3 hover:bg-[#cc0029] disabled:opacity-50 flex items-center justify-center gap-2">
                      {payBusy ? <Loader2 className="animate-spin" size={18} /> : t("Kostenlos buchen (100% Rabatt)", "Book for free (100% off)")}
                    </button>
                  ) : (
                    <>
                      {ppConfig?.enabled && ppConfig?.client_id && (
                        <div className={payBusy ? "opacity-50 pointer-events-none" : ""} data-testid="tm-paypal">
                          <PayPalScriptProvider options={{ clientId: ppConfig.client_id, currency: ppConfig.currency || "EUR", components: "buttons", intent: "capture" }}>
                            <PayPalButtons fundingSource="paypal" forceReRender={[finalTotal, ppConfig.client_id]} style={{ layout: "vertical", color: "gold", shape: "pill", label: "paypal", height: 44 }} disabled={payBusy}
                              onClick={(d, actions) => (validateRef.current() ? actions.resolve() : actions.reject())}
                              createOrder={async () => { const { data } = await api.post("/trackman/checkout/paypal", payloadRef.current()); if (data.free) { setDone({ booking_number: data.booking_number, email: data.email }); return ""; } return data.order_id; }}
                              onApprove={async (d) => { setPayBusy(true); try { const { data: res } = await api.post(`/trackman/paypal/capture/${d.orderID}`); if (res.payment_status === "paid") setDone({ booking_number: res.booking_number, email: res.email }); else toast.error(t("Zahlung nicht abgeschlossen.", "Payment not completed.")); } catch (e) { toast.error(apiError(e)); } finally { setPayBusy(false); } }}
                              onCancel={() => toast.message(t("PayPal abgebrochen – nichts gebucht.", "PayPal cancelled – nothing booked."))}
                              onError={() => toast.error(t("PayPal-Fehler. Bitte mit Karte zahlen.", "PayPal error. Please pay by card."))} />
                          </PayPalScriptProvider>
                          <div className="flex items-center gap-3 my-2"><div className="h-px bg-neutral-200 flex-1" /><span className="text-neutral-400 text-[10px] font-mono-lab uppercase">{t("oder", "or")}</span><div className="h-px bg-neutral-200 flex-1" /></div>
                        </div>
                      )}
                      <button onClick={payStripe} disabled={payBusy} data-testid="tm-pay-stripe" className="w-full bg-[#ff0033] text-white font-heading uppercase tracking-wider py-3 hover:bg-[#cc0029] disabled:opacity-50 flex items-center justify-center gap-2">
                        {payBusy ? <Loader2 className="animate-spin" size={18} /> : `${t("Mit Karte bezahlen", "Pay by card")} · ${euro(finalTotal)}`}
                      </button>
                    </>
                  )}
                  <p className="text-[10px] text-neutral-400">{tm.paymentNote || t("Sichere Vorabzahlung über Stripe/PayPal. Der Slot ist während der Zahlung reserviert. Der Zugangscode wird separat vor Deiner Buchung per E-Mail zugesendet.", "Secure prepayment via Stripe/PayPal. The slot is reserved during payment. Your access code is sent separately by email before your booking.")}</p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const MonthGrid = ({ monthAnchor, dayMap, WD, selectedDay, onPick, t }) => {
  const first = new Date(monthAnchor.getFullYear(), monthAnchor.getMonth(), 1);
  const gridStart = startOfWeek(first);
  const cells = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  return (
    <div data-testid="tm-month-grid">
      <div className="grid grid-cols-7 gap-1 mb-1">{WD.map((w) => <div key={w} className="text-center font-mono-lab text-[10px] uppercase tracking-widest text-neutral-500 py-1">{w}</div>)}</div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d) => {
          const ds = fmtISO(d);
          const inMonth = d.getMonth() === monthAnchor.getMonth();
          const day = dayMap[ds];
          const past = d < new Date(new Date().setHours(0, 0, 0, 0));
          const freeCount = day && !day.closed ? day.slots.filter((s) => s.status === "free").length : 0;
          const hasHours = day && !day.closed && day.slots.length > 0;
          const sel = selectedDay === ds;
          let dot = "bg-white/10";
          if (hasHours && freeCount > 0) dot = "bg-green-500"; else if (hasHours) dot = "bg-[#ff0033]";
          return (
            <button key={ds} disabled={!hasHours || past} onClick={() => onPick(ds)} data-testid={`tm-month-day-${ds}`}
              className={`aspect-square border p-1 flex flex-col items-center justify-center transition-colors ${sel ? "border-[#ff0033] bg-[#ff0033]/15" : "border-white/10"} ${inMonth ? "" : "opacity-30"} ${hasHours && !past ? "hover:border-[#ff0033]/60 cursor-pointer" : "cursor-not-allowed"}`}>
              <span className={`font-heading text-sm ${inMonth ? "text-white" : "text-neutral-600"}`}>{d.getDate()}</span>
              {!past && <span className={`w-1.5 h-1.5 rounded-full mt-1 ${dot}`} />}
              {hasHours && freeCount > 0 && !past && <span className="text-[8px] font-mono-lab text-green-400 mt-0.5">{freeCount} {t("frei", "free")}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const DoneView = ({ done, lang }) => {
  const { t: cms } = useLang();
  const tm = cms.trackman || {};
  const t = (de, en) => (lang === "de" ? de : en);
  return (
    <div className="bg-[#050505] min-h-screen flex items-center justify-center px-4" data-testid="tm-done">
      <div className="max-w-md w-full bg-white text-black p-8 text-center border-t-4 border-[#ff0033]">
        <div className="w-16 h-16 bg-green-500 rounded-full grid place-items-center mx-auto mb-5"><Check size={32} className="text-white" /></div>
        <h2 className="font-heading text-2xl uppercase tracking-tight">{tm.doneTitle || t("Buchung bestätigt", "Booking confirmed")}</h2>
        <p className="text-neutral-600 mt-3">{tm.doneText || t("Vielen Dank! Deine TrackMan-Buchung ist bezahlt und bestätigt.", "Thank you! Your TrackMan booking is paid and confirmed.")}</p>
        <p className="font-mono-lab text-sm mt-4 bg-neutral-100 py-2">{t("Buchungsnummer", "Booking no.")}: <b>{done.booking_number}</b></p>
        <p className="text-neutral-500 text-sm mt-4">{t("Eine Bestätigung und die Rechnung wurden an", "A confirmation and invoice were sent to")} <b>{done.email}</b> {t("gesendet.", "was sent.")} {tm.doneNote || t("Der Zugangscode folgt separat vor Deiner Buchung.", "The access code follows separately before your booking.")}</p>
        <a href="/trackman" className="inline-block mt-6 bg-[#ff0033] text-white font-heading uppercase tracking-wider px-6 py-3 hover:bg-[#cc0029]">{t("Weitere Buchung", "Book again")}</a>
      </div>
    </div>
  );
};

export default TrackManBuchen;
